import { createContext, FC, useCallback, useContext, useState } from 'react';
import { SnackAlert, SnackAlertContent, SnackAlertContentProps } from '@silverfort/sf-components';

type OpenSnackAlertParams = Omit<SnackAlertContentProps, 'open'>;

export interface SnackAlertContextValue {
    openSnackAlert: (params: OpenSnackAlertParams) => void;
    closeSnackAlert: () => void;
}

const SnackAlertContext = createContext<SnackAlertContextValue | undefined>(undefined);

export const SnackAlertProvider: FC = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [snackAlertContent, setSnackAlertContent] = useState<SnackAlertContentProps | undefined>();

    const openSnackAlert: SnackAlertContextValue['openSnackAlert'] = useCallback((params) => {
        setIsOpen(true);
        setSnackAlertContent(params);
    }, []);

    const closeSnackAlert: SnackAlertContextValue['closeSnackAlert'] = useCallback(() => {
        setIsOpen(false);
    }, []);

    const contextValue: SnackAlertContextValue = { openSnackAlert, closeSnackAlert };

    return (
        <SnackAlertContext.Provider value={contextValue}>
            {children}
            <SnackAlert
                open={isOpen}
                autoHideDuration={2000}
                onClose={closeSnackAlert}
                className="absolute"
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}>
                <SnackAlertContent {...snackAlertContent} />
            </SnackAlert>
        </SnackAlertContext.Provider>
    );
};

export const useSnackAlert = () => {
    const context = useContext(SnackAlertContext);

    if (!context) {
        throw new Error('useSnackAlert must be used within a SnackAlertProvider');
    }

    return context;
};
